// Runs one ticket action (resolve, snooze, set_priority…) with the same behaviour as a click
// on the buttons: the screen changes at once, the success toast shows at once, the dispatcher's
// 1-3 s happens in the background, and only a refusal interrupts (the ticket goes back, the toast
// turns into an error with Retry).
//
// No React in here, on purpose: `useTicketAction` wraps it for components, and `after-send.ts`
// calls it from the outbox, long after the composer that queued the reply has unmounted.
// The mechanics (which caches change, ordering, rollback) live in ticket-actions.ts; the action
// logic itself stays in the n8n dispatcher (CLAUDE.md §5).

import type { QueryClient } from '@tanstack/react-query';
import { postAction, type TicketAction } from './api';
import { refreshTicketListsEverywhere } from './queries';
import { prepareTicketAction, type ActionContext } from './ticket-actions';
import { toastActionInstant, toastActionPromise, toastActionRolledBack } from './toast';

let toastSeq = 0;
export const nextActionToastId = () => `ticket-action-${++toastSeq}`;

export interface RunOptions {
  /** Shown after the verb in the toast: "Ticket marked resolved — Subject". */
  subject?: string | null;
}

export function makeActionContext(qc: QueryClient, actorSlackId: string | null): ActionContext {
  return {
    actorSlackId,
    send: (intent) => postAction({ ...intent, action: intent.action as TicketAction }),
    refreshLists: () => refreshTicketListsEverywhere(qc),
  };
}

/** Run one action on one ticket. Resolves `true` once the server confirmed, `false` if it was
 *  refused or unreachable (the toast already says so). It never rejects, so callers can
 *  fire-and-forget. */
export function runTicketAction(
  qc: QueryClient,
  ctx: ActionContext,
  ticketId: string,
  action: TicketAction,
  payload?: Record<string, unknown>,
  opts?: RunOptions,
): Promise<boolean> {
  const subject = opts?.subject ?? undefined;
  const prepared = prepareTicketAction(qc, { ticketId, action, payload }, ctx);

  if (!prepared.patched) {
    // Nothing could be shown early (ticket not loaded, or the action changes no ticket
    // field): keep the loading → result toast.
    const sent = prepared.commit();
    toastActionPromise(action, sent, { ticketSubject: subject }).catch(() => {});
    return sent.then(
      () => true,
      () => false,
    );
  }

  const toastId = nextActionToastId();
  toastActionInstant(action, toastId, { ticketSubject: subject });
  return prepared.commit().then(
    () => true,
    (err: unknown) => {
      toastActionRolledBack(action, toastId, err, {
        onRetry: () => {
          void runTicketAction(qc, ctx, ticketId, action, payload, opts);
        },
      });
      return false;
    },
  );
}
